import { Avatar, Box, Tooltip } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar';
import { UPButtonStyledWithNoPadding } from 'components/button/styles';
import ContractDetailPanel from 'components/contract-detail-panel/ContractDetailPanel';
import DeleteModal from 'components/delete-modal';
import UPTanStackTable from 'components/tanstack-table/UPTanStackTable';
import { TableFooter } from 'components/tanstack-table/table-footer';
import { UPTanStackTableColumn } from 'components/tanstack-table/types';
import { CompanyRole } from 'constants/CompanyRole';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { deleteContractsAction } from 'store/actions/contract-actions';
import { costCentersDropdownItemsSelector } from 'store/selectors/company-cost-centers-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANY_PAYROLL__EMPLOYEE,
  CONTRACTS__ADJUSTED,
  CONTRACTS__ARE_YOU_SURE,
  CONTRACTS__AUTO_CLOSED,
  CONTRACTS__COST_CENTER,
  CONTRACTS__DATE,
  CONTRACTS__DELETE_SELECTED,
  CONTRACTS__DELETE_WEEKLY_CONTRACT_WARNING,
  CONTRACTS__DEVIATION,
  CONTRACTS__EFFECTIVE_TOTAL,
  CONTRACTS__END_BREAK,
  CONTRACTS__END_WORK,
  CONTRACTS__NO_COMPLETED_SERVICES,
  CONTRACTS__START_BREAK,
  CONTRACTS__START_WORK,
  CONTRACTS__SELECTED,
  GENERAL__CLEAR_ALL,
  GENERAL__ERROR_MESSAGE,
  TIME_REGISTRATION__CONFIRMED,
} from 'translations/constants';
import { ReactComponent as DeleteIcon } from '../../assets/icons/DeleteFull.svg';
import { ReactComponent as DeleteIconGreen } from '../../assets/icons/DeleteGreen.svg';
import AbsenceIndicator from './components/AbsenceIndicator';
import BooleanIndicatorIcon from './components/BooleanIndicatorIcon';
import DeviationCell from './components/DeviationCell';
import EditableTimeCell from './components/EditableTimeCell';
import EmployeeAdjustedIndicator from './components/EmployeeAdjustedIndicator';
import RegistrationStatusBadge from './components/RegistrationStatusBadge';
import ViewDetailsButton from './components/ViewDetailsButton';
import { PAGE_SIZE_OPTIONS } from './constants';
import { useCompletedData } from './hooks/useCompletedData';
import { CompletedItem, WeekSelection } from './types';
import { formatSelectionTotal, formatTotalHours } from './utils';

type TimeField = 'startWork' | 'startBreak' | 'endBreak' | 'endWork';

interface CompletedViewProps {
  companyId: number;
  weeks: WeekSelection[];
  date: string | null;
  employeeIds: number[];
  costCenterId: number | null;
  locationId: number | null;
  workerClass: string | null;
  functionId: number | null;
  setSelectedContractsId: (ids: number[]) => void;
}

const noop = () => undefined;

const CompletedView = (props: CompletedViewProps) => {
  const {
    companyId,
    weeks,
    date,
    employeeIds,
    costCenterId,
    locationId,
    workerClass,
    functionId,
    setSelectedContractsId,
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(profileSelector);
  const languageId = profile?.languageId;
  const isAdmin = profile?.role === CompanyRole.ADMIN;
  const costCenters = useAppSelector(costCentersDropdownItemsSelector);

  useEffect(() => {
    if (companyId) {
      dispatch(getCostCentersDropdownItemsAction({ companyId }));
    }
  }, [companyId, dispatch]);

  const {
    items,
    totalItems,
    error,
    clearError,
    pagination,
    setPagination,
    sorting,
    setSorting,
    refetch,
    selectedItems,
    currentPageSelectedRows,
    isAllOnPageSelected,
    isSomeSelected,
    handleSelectAllOnPage,
    handleSelectionChange,
    handleClearAll,
    deselect,
  } = useCompletedData(companyId, weeks, date, employeeIds, costCenterId, locationId, workerClass, functionId);

  const [detailRow, setDetailRow] = useState<CompletedItem | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | number | null>(null);
  const [rowsToDelete, setRowsToDelete] = useState<CompletedItem[]>([]);
  const [openConfirmDeleteWeeklyModal, setOpenConfirmDeleteWeeklyModal] = useState<boolean>(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState<boolean>(false);

  useEffect(() => {
    setSelectedContractsId(Array.from(new Set(selectedItems.map(item => item.contractDateId))));
  }, [selectedItems, setSelectedContractsId]);

  const totalSelectedTime = useMemo(
    () =>
      formatSelectionTotal(
        selectedItems.reduce((sum, item) => sum + (item.noShow ? 0 : item.effectiveTotalMinutes), 0),
      ),
    [selectedItems],
  );

  const onDeleteContracts = useCallback((rows: CompletedItem[]) => {
    setRowsToDelete(rows);
    if (rows.some(row => row.isWeeklyContract)) setOpenConfirmDeleteWeeklyModal(true);
    else setOpenConfirmDeleteModal(true);
  }, []);

  const makeDeleteRequest = useCallback(() => {
    const contractDateIds = new Set(rowsToDelete.map(row => row.contractDateId));
    const deletedRowIds = selectedItems
      .filter(item => contractDateIds.has(item.contractDateId))
      .map(item => item.serviceDateId);
    const [firstWeek] = weeks;
    dispatch(
      deleteContractsAction({
        companyId,
        contractsIds: Array.from(contractDateIds),
        year: firstWeek?.year,
        weekNumber: firstWeek?.week,
      }),
    ).then(() => {
      deselect(deletedRowIds);
      refetch();
    });
    setRowsToDelete([]);
  }, [rowsToDelete, selectedItems, weeks, dispatch, companyId, deselect, refetch]);

  const pageCount = useMemo(() => Math.ceil(totalItems / pagination.pageSize), [totalItems, pagination.pageSize]);

  const footerActions = useMemo(
    () =>
      isAdmin
        ? [
            {
              id: 'delete',
              label: t(CONTRACTS__DELETE_SELECTED),
              icon: <DeleteIcon />,
              onClick: () => onDeleteContracts(selectedItems),
            },
          ]
        : [],
    [isAdmin, t, onDeleteContracts, selectedItems],
  );

  const renderSelectionFooter = useCallback(
    () => (
      <TableFooter
        selectedCount={selectedItems.length}
        totalCount={totalItems}
        actions={footerActions}
        clearAllText={t(GENERAL__CLEAR_ALL)}
        selectedText={t(CONTRACTS__SELECTED)}
        extraText={totalSelectedTime}
        onClearSelection={handleClearAll}
      />
    ),
    [selectedItems.length, totalItems, footerActions, t, totalSelectedTime, handleClearAll],
  );

  const employeeNameCellRenderer = useCallback(
    (info: { row: CompletedItem }) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar src={info.row.employeeAvatar ?? undefined} sx={{ width: 32, height: 32 }} />
        <span>{`${info.row.employeeFirstName} ${info.row.employeeLastName}`}</span>
      </Box>
    ),
    [],
  );

  const dateCellRenderer = useCallback((info: { row: CompletedItem }) => {
    const dateObj = new Date(info.row.date);
    return dateObj.toLocaleDateString('en-GB');
  }, []);

  const renderTimeCell = useCallback(
    (row: CompletedItem, field: TimeField) => (
      <EditableTimeCell
        value={row[field] ?? ''}
        field={field}
        hoursSource={row.hoursSource}
        hasTimeRegistration={row.hasTimeRegistration}
        isEmployeeAdjusted={row.isEmployeeAdjusted}
        isEditable={false}
        onSave={noop}
        hideArrow
      />
    ),
    [],
  );

  const startWorkCellRenderer = useCallback(
    (info: { row: CompletedItem }) => {
      if (info.row.noShow) {
        return <AbsenceIndicator reason={info.row.noShowReasonDetail} />;
      }
      return renderTimeCell(info.row, 'startWork');
    },
    [renderTimeCell],
  );

  const startBreakCellRenderer = useCallback(
    (info: { row: CompletedItem }) => (info.row.noShow ? null : renderTimeCell(info.row, 'startBreak')),
    [renderTimeCell],
  );

  const endBreakCellRenderer = useCallback(
    (info: { row: CompletedItem }) => (info.row.noShow ? null : renderTimeCell(info.row, 'endBreak')),
    [renderTimeCell],
  );

  const endWorkCellRenderer = useCallback(
    (info: { row: CompletedItem }) => (info.row.noShow ? null : renderTimeCell(info.row, 'endWork')),
    [renderTimeCell],
  );

  const effectiveTotalCellRenderer = useCallback(
    (info: { row: CompletedItem }) =>
      formatTotalHours(info.row.noShow ? 0 : info.row.effectiveTotalMinutes, languageId),
    [languageId],
  );

  const deviationCellRenderer = useCallback((info: { row: CompletedItem }) => {
    if (!info.row.hasPlanning) return <BooleanIndicatorIcon value={false} />;
    return <DeviationCell deviationMinutes={info.row.deviationMinutes} />;
  }, []);

  const adjustedCellRenderer = useCallback((info: { row: CompletedItem }) => {
    if (info.row.noShow) return <BooleanIndicatorIcon value={false} />;
    return <EmployeeAdjustedIndicator isEmployeeAdjusted={info.row.isEmployeeAdjusted} />;
  }, []);

  const costCenterCellRenderer = useCallback(
    (info: { row: CompletedItem }) => {
      // eslint-disable-next-line eqeqeq
      const costCenter = costCenters.find(cc => cc.value == info.row.costCenterId);
      return <span>{info.row.costCenterName ?? costCenter?.label ?? ''}</span>;
    },
    [costCenters],
  );

  const statusCellRenderer = useCallback(
    (info: { row: CompletedItem }) => (
      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
        <Tooltip title={t(TIME_REGISTRATION__CONFIRMED)}>
          <span>
            <RegistrationStatusBadge hasTimeRegistration status="Confirmed" />
          </span>
        </Tooltip>
        {info.row.isAutoClosed && (
          <Box component="span" sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {t(CONTRACTS__AUTO_CLOSED)}
          </Box>
        )}
      </Box>
    ),
    [t],
  );

  const viewDetailsCellRenderer = useCallback(
    (info: { row: CompletedItem }) => <ViewDetailsButton isHovered onClick={() => setDetailRow(info.row)} />,
    [],
  );

  const deleteCellRenderer = useCallback(
    (info: { row: CompletedItem }) => {
      if (hoveredRowId !== info.row.serviceDateId) return null;
      return (
        <UPButtonStyledWithNoPadding onClick={() => onDeleteContracts([info.row])}>
          <DeleteIconGreen />
        </UPButtonStyledWithNoPadding>
      );
    },
    [hoveredRowId, onDeleteContracts],
  );

  const columns = useMemo<UPTanStackTableColumn<CompletedItem>[]>(
    () => [
      {
        id: 'employeeName',
        header: t(COMPANY_PAYROLL__EMPLOYEE),
        accessorKey: 'employeeLastName',
        enableSorting: true,
        size: 200,
        cell: employeeNameCellRenderer,
      },
      {
        id: 'date',
        header: t(CONTRACTS__DATE),
        accessorKey: 'date',
        enableSorting: true,
        size: 120,
        cell: dateCellRenderer,
      },
      {
        id: 'startWork',
        header: t(CONTRACTS__START_WORK),
        accessorKey: 'startWork',
        enableSorting: true,
        size: 140,
        cell: startWorkCellRenderer,
        colSpan: (row: CompletedItem) => (row.noShow ? 4 : 1),
      },
      {
        id: 'startBreak',
        header: t(CONTRACTS__START_BREAK),
        accessorKey: 'startBreak',
        enableSorting: true,
        size: 140,
        cell: startBreakCellRenderer,
      },
      {
        id: 'endBreak',
        header: t(CONTRACTS__END_BREAK),
        accessorKey: 'endBreak',
        enableSorting: true,
        size: 140,
        cell: endBreakCellRenderer,
      },
      {
        id: 'endWork',
        header: t(CONTRACTS__END_WORK),
        accessorKey: 'endWork',
        enableSorting: true,
        size: 140,
        cell: endWorkCellRenderer,
      },
      {
        id: 'effectiveTotalMinutes',
        header: t(CONTRACTS__EFFECTIVE_TOTAL),
        accessorKey: 'effectiveTotalMinutes',
        enableSorting: true,
        size: 120,
        align: 'center',
        headerAlign: 'center',
        cell: effectiveTotalCellRenderer,
      },
      {
        id: 'deviationMinutes',
        header: t(CONTRACTS__DEVIATION),
        accessorKey: 'deviationMinutes',
        enableSorting: true,
        size: 120,
        headerAlign: 'center',
        align: 'center',
        cell: deviationCellRenderer,
      },
      {
        id: 'isEmployeeAdjusted',
        header: t(CONTRACTS__ADJUSTED),
        enableSorting: false,
        size: 80,
        align: 'center',
        headerAlign: 'center',
        cell: adjustedCellRenderer,
      },
      {
        id: 'costCenterId',
        header: t(CONTRACTS__COST_CENTER),
        accessorKey: 'costCenterId',
        enableSorting: true,
        headerAlign: 'center',
        align: 'center',
        size: 160,
        cell: costCenterCellRenderer,
      },
      {
        id: 'registrationStatus',
        header: '',
        enableSorting: false,
        size: 120,
        align: 'center',
        cell: statusCellRenderer,
      },
      {
        id: 'viewDetails',
        header: '',
        enableSorting: false,
        size: 120,
        cell: viewDetailsCellRenderer,
      },
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              enableSorting: false,
              size: 60,
              align: 'right' as const,
              cell: deleteCellRenderer,
            },
          ]
        : []),
    ],
    [
      t,
      isAdmin,
      employeeNameCellRenderer,
      dateCellRenderer,
      startWorkCellRenderer,
      startBreakCellRenderer,
      endBreakCellRenderer,
      endWorkCellRenderer,
      effectiveTotalCellRenderer,
      deviationCellRenderer,
      adjustedCellRenderer,
      costCenterCellRenderer,
      statusCellRenderer,
      viewDetailsCellRenderer,
      deleteCellRenderer,
    ],
  );

  const customHeaderCheckbox = useMemo(
    () => ({
      checked: isAllOnPageSelected,
      indeterminate: isSomeSelected,
      onChange: handleSelectAllOnPage,
    }),
    [isAllOnPageSelected, isSomeSelected, handleSelectAllOnPage],
  );

  const getRowId = useCallback((row: CompletedItem) => row.serviceDateId, []);

  return (
    <Box>
      <UPSnackBar
        open={!!error}
        onClose={clearError}
        content={t(GENERAL__ERROR_MESSAGE)}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      <UPTanStackTable<CompletedItem>
        data={error ? [] : items}
        columns={columns}
        getRowId={getRowId}
        emptyMessage={error ? t(GENERAL__ERROR_MESSAGE) : t(CONTRACTS__NO_COMPLETED_SERVICES)}
        enableRowSelection
        selectedRows={currentPageSelectedRows}
        onSelectionChange={handleSelectionChange}
        customHeaderCheckbox={customHeaderCheckbox}
        onRowHover={setHoveredRowId}
        enableSorting
        manualSorting
        sorting={sorting}
        onSortingChange={setSorting}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={pageCount}
        totalItems={totalItems}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        renderSelectionFooter={renderSelectionFooter}
      />
      {openConfirmDeleteWeeklyModal && (
        <DeleteModal
          open={openConfirmDeleteWeeklyModal}
          content={t(CONTRACTS__DELETE_WEEKLY_CONTRACT_WARNING)}
          onClose={() => {
            setOpenConfirmDeleteWeeklyModal(false);
          }}
          onDelete={() => {
            setOpenConfirmDeleteModal(true);
          }}
        />
      )}
      {openConfirmDeleteModal && (
        <DeleteModal
          open={openConfirmDeleteModal}
          content={t(CONTRACTS__ARE_YOU_SURE)}
          onClose={() => {
            setOpenConfirmDeleteModal(false);
          }}
          onDelete={makeDeleteRequest}
        />
      )}
      <ContractDetailPanel
        contractDateId={detailRow?.contractDateId ?? null}
        serviceDateId={detailRow?.serviceDateId ?? null}
        companyId={companyId}
        onClose={() => setDetailRow(null)}
        onSaveSuccess={noop}
        mode="readOnly"
      />
    </Box>
  );
};

export default CompletedView;
